import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styles from './VideoGrid.module.scss';
import VideoTile from '../VideoTile/VideoTile';
import { calculateTileLayout } from '../../../utils/calcTileSize';
import { CALLKIT_MAXTILES, CALLKIT_VIEWMODE } from '../../../constants';

/**
 * Props:
 * - participants: array (provided by useCallConnectedScreen - already truncated if required)
 * - viewMode: 'auto'|'tiled'|'spotlight'|'sidebar'
 * - primary: object (presenter/pinned) or null
 * - activeSpeakerId
 * - pinnedId
 * - handlePin
 * - onParticipantsPanel: callback to open side panel
 * - containerRef: ref to .videoArea element (required)
 */
export default function VideoGrid({
   participants = [],
   viewMode = CALLKIT_VIEWMODE.AUTO,
   primary = null,
   pinnedId = null,
   activeSpeakerId = null,
   handlePin,
   onParticipantsPanel,
   containerRef,
   hasSidePanel = false,
}) {
   const [layout, setLayout] = useState({
      tileWidth: 0,
      tileHeight: 0,
      columns: 1,
      rows: 1,
      rowConfig: [],
   });

   // update layout on resize or participant count change
   const updateLayout = useCallback(() => {
      if (!containerRef?.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();

      const countForCalc = (() => {
         if (viewMode === CALLKIT_VIEWMODE.SPOTLIGHT) return 1;
         return Math.min(CALLKIT_MAXTILES, Math.max(1, participants.length));
      })();

      const paddingX = (() => {
         if (hasSidePanel) return 40;
         const count = participants.length;
         if (width < 768) {
            if (count <= 2) return 20;
            if (count <= 4) return 16;
            return 12;
         }
         if (count <= 2) return 80;
         if (count <= 4) return 60;
         if (count <= 6) return 40;
         return 20;
      })();
      const paddingY = hasSidePanel ? 16 : 20;

      const layoutData = calculateTileLayout({
         participantCount: countForCalc,
         containerWidth: Math.max(0, width - paddingX),
         containerHeight: Math.max(0, height - paddingY),
         aspectRatio: 16 / 9,
         gap: 24,
         fillMode: participants.length >= CALLKIT_MAXTILES || [2, 3, 5, 6, 7].includes(participants.length),
      });

      layoutData.sidebarWidth = Math.max(120, (width || 1200) * 0.15);
      layoutData.isMobile = width < 768;
      setLayout(layoutData);
   }, [participants.length, viewMode, hasSidePanel, containerRef]);

   // Handle window resize
   useEffect(() => {
      if (!containerRef?.current) return;
      const observer = new ResizeObserver(() => {
         updateLayout();
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
   }, [updateLayout, containerRef]);

   // Handle participant / mode changes immediately before paint
   useLayoutEffect(() => {
      updateLayout();
   }, [updateLayout]);

   // 1) Spotlight Mode
   if (viewMode === CALLKIT_VIEWMODE.SPOTLIGHT) {
      const who = primary || participants[0] || null;
      if (!who) return null;
      return (
         <div className={`${styles.spotlight} ct-flexCenter`}>
            <div className={`${styles.spotlightInner} ct-flexCenter`}>
               <div className={styles.fullTile}>
                  <VideoTile
                     participant={who}
                     isPrimary
                     isPinned={who.id === pinnedId}
                     isActiveSpeaker={who.id === activeSpeakerId}
                     handlePin={handlePin}
                  />
               </div>
            </div>
         </div>
      );
   }

   // 2) Sidebar Mode (Tile View)
   if (viewMode === CALLKIT_VIEWMODE.SIDEBAR && primary) {
      const isMobile = layout.isMobile;
      const sidebarWidthPx = isMobile ? '100%' : `${layout.sidebarWidth || 120}px`;
      const sidebarHeightPx = isMobile ? 'auto' : '100%';

      const sideTileWidth = isMobile ? Math.min(160, layout.sidebarWidth || 120) : (layout.sidebarWidth || 120) - 16;
      const sideTileHeight = sideTileWidth / (16 / 9);
      const mainParticipant = primary;

      return (
         <div className={`${styles.sidebarLayout} ct-flex`}>
            <div className={`${styles.sidebarMain} ct-flexCenter`}>
               <div style={{ width: '100%', height: '100%' }}>
                  <VideoTile
                     participant={mainParticipant}
                     isPrimary
                     isPinned={mainParticipant.id === pinnedId}
                     isActiveSpeaker={mainParticipant.id === activeSpeakerId}
                     handlePin={handlePin}
                  />
               </div>
            </div>

            <div
               className={`${styles.sidebarList} ct-flex ${isMobile ? 'ct-frow' : 'ct-fcol'}`}
               style={{ width: sidebarWidthPx, height: sidebarHeightPx }}
            >
               {participants
                  .filter(p => p.id !== mainParticipant.id)
                  .map(p => (
                     <div key={p.id} style={{ width: sideTileWidth, height: sideTileHeight, flexShrink: 0 }}>
                        <VideoTile
                           participant={p}
                           handlePin={handlePin}
                           onOverflowClick={onParticipantsPanel}
                           isPinned={p.id === pinnedId}
                           isActiveSpeaker={p.id === activeSpeakerId}
                           isSidebar
                        />
                     </div>
                  ))}
            </div>
         </div>
      );
   }

   // 3) Grid Mode (Tiled)
   // Grid mode uses the participants provided by the viewmodel (which includes overflow tiles if needed)
   const countClass = (() => {
      const count = participants.length;
      if (count <= 2) return styles.count2;
      if (count <= 4) return styles.count4;
      if (count <= 6) return styles.count6;
      return styles.count8;
   })();

   return (
      <div
         className={`${styles.main} ${countClass} ${hasSidePanel ? styles.withSidePanel : ''} ct-flex ct-fwrap ct-jcCenter ct-aiCenter`}
      >
         {participants.map((p, index) => (
            <div
               key={p.id || index}
               style={{
                  width: layout.tileWidth,
                  height: layout.tileHeight,
               }}
            >
               <VideoTile
                  participant={p}
                  handlePin={handlePin}
                  onOverflowClick={onParticipantsPanel}
                  isPinned={p.id === pinnedId}
                  isPrimary={p.id === primary?.id}
                  isActiveSpeaker={p.id === activeSpeakerId}
               />
            </div>
         ))}
      </div>
   );
}

VideoGrid.propTypes = {
   participants: PropTypes.arrayOf(
      PropTypes.shape({
         id: PropTypes.string.isRequired,
         name: PropTypes.string,
         isLocal: PropTypes.bool,
         isVideoMuted: PropTypes.bool,
         isAudioMuted: PropTypes.bool,
         callStatus: PropTypes.string,
      }),
   ),
   viewMode: PropTypes.oneOf([
      CALLKIT_VIEWMODE.AUTO,
      CALLKIT_VIEWMODE.TILED,
      CALLKIT_VIEWMODE.SPOTLIGHT,
      CALLKIT_VIEWMODE.SIDEBAR,
   ]),
   primary: PropTypes.shape({
      id: PropTypes.string,
      isLocal: PropTypes.bool,
   }),
   activeSpeakerId: PropTypes.string,
   pinnedId: PropTypes.string,
   handlePin: PropTypes.func,
   onParticipantsPanel: PropTypes.func,
   containerRef: PropTypes.shape({
      current: PropTypes.instanceOf(Element),
   }),
   hasSidePanel: PropTypes.bool,
};
